import type { QueryClient } from "@tanstack/react-query";

export const TAX_RETURN_SAVE_STORAGE_KEY = "taxReturnPendingSave:v1";
type SaveStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;
type PendingSave = { version: 1; scope: string; fingerprint: string; key: string };
type SaveActor = {
  id: string;
  ownerUserId?: string | null;
  impersonatingUserId?: string | null;
};

export function taxReturnSaveScope(user: SaveActor | null | undefined): string {
  return user?.id ? JSON.stringify([user.id, user.impersonatingUserId ?? user.ownerUserId ?? user.id]) : "";
}

function browserStorage(): SaveStorage | null {
  try { return typeof window === "undefined" ? null : window.sessionStorage; }
  catch { return null; }
}

/** Persist identity only, never names, paths, snapshots or serialized payloads. */
export class TaxReturnSaveRequest {
  private pending: PendingSave | null = null;
  private scope: string | undefined;
  private revision = 0;
  private queue: Promise<unknown> = Promise.resolve();
  private storageReadable = false;
  private recoveryUnavailable = false;
  private listeners = new Set<() => void>();

  constructor(private storage: SaveStorage | null = browserStorage()) {}

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  getRecoveryUnavailable = (): boolean => this.recoveryUnavailable;

  private setRecoveryUnavailable(value: boolean): void {
    if (this.recoveryUnavailable === value) return;
    this.recoveryUnavailable = value;
    this.listeners.forEach(listener => listener());
  }

  setScope(scope: string): void {
    if (scope === this.scope) return;
    this.revision++;
    this.scope = scope;
    this.pending = null;
    this.setRecoveryUnavailable(false);
    this.storageReadable = false;
    let raw: string | null | undefined;
    try {
      raw = this.storage?.getItem(TAX_RETURN_SAVE_STORAGE_KEY);
      this.storageReadable = this.storage !== null;
    } catch {
      // Blocked reads also prevent recovery, even if writes are allowed.
    }
    try {
      if (raw && scope) {
        const saved = JSON.parse(raw);
        if (saved.version === 1 && saved.scope === scope &&
            typeof saved.key === "string" && /^[0-9a-f-]{36}$/i.test(saved.key) &&
            typeof saved.fingerprint === "string" && /^[0-9a-f]{64}$/.test(saved.fingerprint)) {
          this.pending = { version: 1, scope, key: saved.key, fingerprint: saved.fingerprint };
          return;
        }
      }
    } catch {
      // Corrupt or blocked storage must not prevent an in-memory retry.
    }
    this.removeStored();
  }

  keyFor(scope: string, payload: unknown): Promise<string> {
    if (!scope) return Promise.reject(new Error("Sign in before saving a tax return"));
    this.setScope(scope);
    const revision = this.revision;
    const serialized = JSON.stringify(payload);
    const request = this.queue.then(async () => {
      // Salt the digest with the random request key. It is comparison identity,
      // not a retained copy of any financial or document metadata.
      const key = this.pending?.key ?? crypto.randomUUID();
      const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(`${key}:${serialized}`));
      if (revision !== this.revision) throw new Error("Account changed while preparing tax return save");
      const fingerprint = Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, "0")).join("");
      if (!this.pending || this.pending.fingerprint !== fingerprint) {
        // On an edit, recompute with a fresh salt as well as a fresh key.
        if (this.pending) {
          this.pending = null;
          return this.create(scope, serialized, revision);
        }
        this.pending = { version: 1, scope, fingerprint, key };
        this.persist();
      }
      return this.pending.key;
    });
    this.queue = request.catch(() => {});
    return request;
  }

  private async create(scope: string, serialized: string, revision: number): Promise<string> {
    const key = crypto.randomUUID();
    const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(`${key}:${serialized}`));
    if (revision !== this.revision) throw new Error("Account changed while preparing tax return save");
    const fingerprint = Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, "0")).join("");
    this.pending = { version: 1, scope, fingerprint, key };
    this.persist();
    return key;
  }

  complete(scope: string, key: string): void {
    if (this.pending?.scope !== scope || this.pending.key !== key) return;
    this.revision++;
    this.pending = null;
    this.setRecoveryUnavailable(false);
    this.removeStored();
  }

  isCurrent(scope: string): boolean {
    return this.scope === scope;
  }

  private persist(): void {
    let stored = false;
    try {
      if (this.storage) {
        this.storage.setItem(TAX_RETURN_SAVE_STORAGE_KEY, JSON.stringify(this.pending));
        stored = this.storageReadable;
      }
    } catch {
      // Keep the pending identity mounted; never log storage errors or payloads.
    }
    this.setRecoveryUnavailable(!stored);
  }

  private removeStored(): void {
    try { this.storage?.removeItem(TAX_RETURN_SAVE_STORAGE_KEY); }
    catch { console.warn("[TaxReturnSave] Could not clear browser retry identity"); }
  }
}

export const taxReturnSaveRequest = new TaxReturnSaveRequest();

// Observe auth even while Operations is unmounted. Undefined on initial load
// is not logout: wait for confirmed auth before accepting or clearing recovery.
export function bindTaxReturnSaveSession(client: QueryClient, request = taxReturnSaveRequest) {
  const sync = (user: SaveActor | null | undefined) => {
    if (user !== undefined) request.setScope(taxReturnSaveScope(user));
  };
  sync(client.getQueryData(["/api/auth/me"]));
  return client.getQueryCache().subscribe(event => {
    if (event.query.queryKey[0] !== "/api/auth/me") return;
    if (event.type === "removed") request.setScope("");
    else if (event.type === "updated" && event.action.type === "success") sync(event.query.state.data as SaveActor | null);
    else if (event.type === "updated" && event.action.type === "error" &&
        (event.query.state.error as { status?: number } | null)?.status === 401) request.setScope("");
  });
}
